import * as React from "react"
import styled from "styled-components"
import { SlideOutHeight } from "../atoms/animations/SlideOutHeight"
import LoadingSpinner from "../atoms/LoadingSpinner"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import { useGetTopupPackages } from "../../hooks-saleor/useGetTopupPackages"
import H3 from "../atoms/typography/H3"
import Subtitle from "../atoms/typography/Subtitle"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { CrossIcon } from "../atoms/icons/CrossIcon"
import { theme } from "../theme"
import SecondaryButton from "../atoms/buttons/SecondaryButton"
import { useCreateCheckoutMutation } from "../../hooks-saleor/checkoutCreateMutation"
import { useMyOrganization } from "../../hooks/useMyOrganization"
import { useUser } from "../../hooks/useUser"
import { CheckoutLineInput } from "../interfaces/SaleorTypes"
import { createPortal } from "react-dom"
import { AnimatePresence } from "framer-motion"
import Modal from "./Modals/Modal"
import { PayTopupModalContentStripePlugin, PayTopupModalContentStripeApp } from "./Modals/PayTopupModalContent"
import { useStripeTransactionInitialize } from "../../hooks-saleor/useStripeTransactionInitialize"
import { useCheckoutPaymentCreate } from "../../hooks-saleor/useCheckoutPaymentCreate"
import { useOrderUpdateMetadataMutation } from "../../hooks-saleor/orderUpdateMetadataMutation"
import { useCheckoutCompleteMutation } from "../../hooks-saleor/checkoutCompleteMutation"
import { SELECTED_CHANNEL_SLUG, IS_USING_STRIPE_PLUGIN_GATEWAY } from "../../utils/saleor-constants"
import { captureExceptionWithContext } from "../../utils/sentry"

interface Props {
  onClickCancel: () => void
  apiPlanId: number | string
  dataPlanName?: string
  isOpen: boolean
}

export interface TopupPackage {
  name: string
  price: number
  checkoutLineInput: CheckoutLineInput
}

const PriceItemSection = styled.div<{ selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: ${(props) => props.theme.borders.softRadius};
  padding: ${(props) => props.theme.spacings.large};
  cursor: pointer;
  background-color: ${(props) => (props.selected ? props.theme.colors.greyDark : undefined)};

  :hover {
    background-color: ${(props) => (props.selected ? undefined : props.theme.colors.white)};
    border: 1px solid black;
  }
`

const ContentContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

const DetailsContainer = styled.div`
  padding: ${(props) => props.theme.spacings.large};
`

const AddDataSection: React.FunctionComponent<Props> = ({ onClickCancel, isOpen, apiPlanId, dataPlanName }) => {
  const { topupPackages, topupListLoading } = useGetTopupPackages()
  const [selectedTopupPackage, setSelectedTopupPackage] = React.useState<TopupPackage>()
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [clientSecret, setClientSecret] = React.useState<string>()
  const [checkoutId, setCheckoutId] = React.useState<string>()
  const { myOrganization } = useMyOrganization()
  const { user } = useUser()
  const { createCheckoutMutation } = useCreateCheckoutMutation()
  const { checkoutPaymentCreate } = useCheckoutPaymentCreate()
  const { checkoutCompleteMutation } = useCheckoutCompleteMutation()
  const { stripeTransactionInitialize } = useStripeTransactionInitialize()
  const { orderUpdateMetadataMutation } = useOrderUpdateMetadataMutation()
  const [isLoading, setIsLoading] = React.useState(false)
  const [transactionId, setTransactionId] = React.useState<string>()

  const onClose = () => {
    setIsModalOpen(false)
    // reset state
    setSelectedTopupPackage(undefined)
    setClientSecret(undefined)
    setCheckoutId(undefined)
    setTransactionId(undefined)
  }

  const onTopup = async () => {
    setIsLoading(true)
    try {
      // create checkout
      const createCheckoutResponse = await createCheckoutMutation({
        variables: {
          channel: SELECTED_CHANNEL_SLUG,
          email: user?.email,
          lines: [selectedTopupPackage?.checkoutLineInput],
          bFirstName: user.firstName,
          bLastName: user.lastName,
          bStreetAdress1: myOrganization?.billingAddress?.address1,
          bStreetAdress2: myOrganization?.billingAddress?.address2,
          bPostalCode: myOrganization?.billingAddress?.zipCode,
          bCity: myOrganization?.billingAddress?.city,
          bCountry: myOrganization?.billingAddress?.country,
          bPhone: "",
          bCompany: myOrganization?.name,
          sFirstName: user.firstName,
          sLastName: user.lastName,
          sStreetAdress1: myOrganization?.billingAddress?.address1,
          sStreetAdress2: myOrganization?.billingAddress?.address2,
          sCity: myOrganization?.billingAddress?.city,
          sPostalCode: myOrganization?.billingAddress?.zipCode,
          sCountry: myOrganization?.billingAddress?.country,
          sPhone: "",
        },
      })
      let scopedCheckoutId = createCheckoutResponse.data.checkoutCreate.checkout.id
      setCheckoutId(scopedCheckoutId)

      // update metadata
      await orderUpdateMetadataMutation({
        variables: {
          orderId: scopedCheckoutId,
          input: [
            { key: "apiPlanId", value: apiPlanId.toString() },
            { key: "checkoutType", value: "topup" },
          ],
        },
      })

      // create payment
      if (IS_USING_STRIPE_PLUGIN_GATEWAY) {
        await checkoutPaymentCreate({
          checkoutId: scopedCheckoutId,
          amount: selectedTopupPackage?.price,
        }).then((response) => {
          checkoutCompleteMutation({
            variables: {
              checkoutId: scopedCheckoutId,
              paymentData: JSON.stringify({
                payment_method_types: ["card"],
              }),
            },
          }).then((response) => {
            setClientSecret(JSON.parse(response.data.checkoutComplete.confirmationData).client_secret)
          })
        })
      } else {
        await stripeTransactionInitialize({
          checkoutId: scopedCheckoutId,
          amount: selectedTopupPackage?.price,
          paymentMethodTypes: ["card"],
        }).then((response) => {
          setClientSecret(response.data?.transactionInitialize?.data?.paymentIntent?.client_secret)
          setTransactionId(response.data?.transactionInitialize?.transaction?.id)
        })
      }
    } catch (error) {
      captureExceptionWithContext(error, "Error creating topup checkout", user?.id)
    }
    setIsModalOpen(true)
    setIsLoading(false)
  }

  return (
    <SlideOutHeight isVisible={isOpen}>
      <DetailsContainer>
        <ContentContainer>
          <div>
            <HorizontalSpacer spacing={3} />
            <Subtitle textAlign="center">Add data to {dataPlanName}</Subtitle>
            <HorizontalSpacer spacing={2} />
            <H3 textAlign="center">Choose data amount:</H3>
            <HorizontalSpacer spacing={2} />
            {topupListLoading ? (
              <LoadingSpinner />
            ) : (
              <>
                {topupPackages?.map((alternative: { name: any; pricing: any; id: any }) => (
                  <PriceItemSection
                    key={alternative.name}
                    onClick={() =>
                      setSelectedTopupPackage({
                        name: alternative.name,
                        price: alternative.pricing?.price?.gross?.amount,
                        checkoutLineInput: {
                          quantity: 1,
                          variantId: alternative.id,
                        },
                      })
                    }
                    selected={alternative.id === selectedTopupPackage?.checkoutLineInput?.variantId}
                  >
                    <H3
                      color={
                        alternative.id === selectedTopupPackage?.checkoutLineInput?.variantId
                          ? theme.colors.white
                          : undefined
                      }
                    >
                      {alternative.name}
                    </H3>
                    <H3
                      color={
                        alternative.id === selectedTopupPackage?.checkoutLineInput?.variantId
                          ? theme.colors.white
                          : undefined
                      }
                    >
                      {alternative.pricing?.price?.gross?.amount}
                      {alternative.pricing?.price?.gross?.currency === "EUR"
                        ? "€"
                        : " " + alternative.pricing?.price?.gross?.currency}
                    </H3>
                  </PriceItemSection>
                ))}
              </>
            )}
            <HorizontalSpacer spacing={3} />
            <div style={{ display: "flex" }}>
              {isLoading ? (
                <LoadingSpinner smallSpinner />
              ) : (
                <SecondaryButton disabled={!selectedTopupPackage} title="Place order" onClick={onTopup} />
              )}
              <Spacer spacing={1} />
              <PrimaryButton title="Cancel" icon={<CrossIcon />} onClick={onClickCancel} />
            </div>
            <HorizontalSpacer spacing={1.5} />
          </div>
          {isModalOpen &&
            selectedTopupPackage &&
            clientSecret &&
            checkoutId &&
            createPortal(
              <AnimatePresence>
                <Modal open={isOpen} onClose={onClose} closeIcon>
                  {IS_USING_STRIPE_PLUGIN_GATEWAY ? (
                    <PayTopupModalContentStripePlugin
                      checkoutId={checkoutId}
                      clientSecret={clientSecret}
                      topupPackage={selectedTopupPackage}
                      dataPlanId={apiPlanId}
                    />
                  ) : (
                    transactionId && (
                      <PayTopupModalContentStripeApp
                        checkoutId={checkoutId}
                        clientSecret={clientSecret}
                        topupPackage={selectedTopupPackage}
                        dataPlanId={apiPlanId}
                        transactionId={transactionId}
                      />
                    )
                  )}
                </Modal>
              </AnimatePresence>,
              document.body,
            )}
        </ContentContainer>
      </DetailsContainer>
    </SlideOutHeight>
  )
}

export default AddDataSection
